import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Stage, Layer, Line, Circle, Rect, Arrow, Text, Image as KonvaImage } from 'react-konva';
import { toast } from 'sonner';
import { usePlanEditor, wallStyleFor, openingStyleFor, roomStyleFor, fixtureKindFor, furnitureKindFor, openingSpecFor, GLAZED_OPENING_TYPES } from './PlanEditorContext';
import { useCanvasViewport, useFitTransform } from '../canvas/useCanvasViewport';
import { buildFloorFit, rotatePolygon, similarityFromPairs } from './floorFit';
import { CanvasZoomControls } from '../canvas/CanvasZoomControls';

/**
 * Native HTMLImageElement loader — same pattern as
 * `blueprint/hooks/useSourceImage.js`. Avoids adding a `use-image`
 * dependency just for this single canvas.
 */
const useNativeImage = (url) => {
  const [img, setImg] = useState(null);
  useEffect(() => {
    if (!url) { setImg(null); return undefined; }
    const i = new window.Image();
    i.crossOrigin = 'use-credentials';
    i.onload = () => setImg(i);
    i.onerror = () => setImg(null);
    i.src = url;
    return () => { i.onload = null; i.onerror = null; };
  }, [url]);
  return img;
};

/**
 * StructureCanvas — V14.47 Sprint 2 + V14.48 Sprint 3.
 *
 * Konva-based interactive canvas for the Structure tab. Displays :
 *   • the cleaned (or raw) plan image as background
 *   • the outer contour polygon in gold (dashed)
 *   • each interior wall as a colored line (cyan/red/purple by type)
 *   • each opening (menuiserie) as a rotated rect with type-specific color
 *
 * Interaction depends on `activeSubTab` :
 *   • 'walls'    → modes select / draw / erase apply to walls
 *   • 'openings' → modes select / 'place:<type>' apply to openings
 *
 * Coordinates : all geometry is stored in [0..1] normalized of the source
 * image, scaled to stage pixels via the current image-fit transform.
 */
const StructureCanvas = ({ bgImageUrl }) => {
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const [size, setSize] = useState({ width: 800, height: 600 });
  const bgImage = useNativeImage(bgImageUrl || '');
  const [draftStart, setDraftStart] = useState(null);   // for walls 'draw' mode
  // V14.80 — Saisie de longueur au clavier pendant le tracé (geste CAD) :
  // taper « 3.5 » + Entrée place le point suivant exactement à 3.50 m dans
  // la direction du curseur. Actif uniquement sur plan calibré.
  const [lengthInput, setLengthInput] = useState('');
  const lengthInputRef = useRef('');
  useEffect(() => { lengthInputRef.current = lengthInput; }, [lengthInput]);

  const {
    outerPolygon, walls, openings, rooms, stairs, fixtures, selectedFixtureId, setSelectedFixtureId, furniture, selectedFurnitureId, setSelectedFurnitureId, terrainPolygon, mode, activeSubTab,
    selectedWallId, setSelectedWallId, hoveredWallId, setHoveredWallId,
    selectedOpeningId, setSelectedOpeningId, hoveredOpeningId, setHoveredOpeningId,
    selectedRoomId, setSelectedRoomId, hoveredRoomId, setHoveredRoomId,
    selectedStairsId, setSelectedStairsId,
    skewEdges, selectedSkewId, setSelectedSkewId,
    stageRef: ctxStageRef,
    ghostStructures,
    polygonDraft, draftHint, setDraftHint,
    endpointSnap, endpointSnapRadius,
    drawWallType, wallFaceSnap, defaultWallThicknessPx, planDefaults,
    menuiserieSnap, menuiserieSnapRadius,
    viewport, setViewport,
    calibration, setImgDims, imgDims, floorOverlay, bgOpacity, twoPoint, setTwoPoint,
    showDimensions,
    actions,
  } = usePlanEditor();

  // V14.78 — expose bg image dims to the context (cm ↔ px plan conversions).
  useEffect(() => {
    if (bgImage?.naturalWidth) {
      setImgDims({ w: bgImage.naturalWidth, h: bgImage.naturalHeight });
    }
  }, [bgImage, setImgDims]);
  // m par unité normalisée — null si non calibré.
  const scaleMPerNorm = calibration?.scale_m_per_norm_unit || null;

  // V14.80 — commit de la longueur tapée : place le point suivant à
  // exactement N mètres dans la direction du curseur (draftHint).
  const lengthEntryActive = !!scaleMPerNorm && (
    (activeSubTab === 'walls' && mode === 'draw' && !!draftStart)
    || (mode === 'draw-polyline-walls' && polygonDraft.length >= 1)
  );
  const commitLengthEntry = useCallback(() => {
    const meters = parseFloat(lengthInputRef.current);
    if (!Number.isFinite(meters) || meters <= 0 || meters > 100 || !scaleMPerNorm) return;
    const dNorm = meters / scaleMPerNorm;
    if (mode === 'draw-polyline-walls' && polygonDraft.length >= 1 && draftHint) {
      const [lx, ly] = polygonDraft[polygonDraft.length - 1];
      const dx = draftHint[0] - lx;
      const dy = draftHint[1] - ly;
      const len = Math.hypot(dx, dy);
      if (len < 1e-9) return;
      actions.pushPolygonDraftPoint(lx + (dx / len) * dNorm, ly + (dy / len) * dNorm);
    } else if (mode === 'draw' && draftStart && draftStart._hint) {
      const dx = draftStart._hint.x - draftStart.x;
      const dy = draftStart._hint.y - draftStart.y;
      const len = Math.hypot(dx, dy);
      if (len < 1e-9) return;
      actions.addWall(
        draftStart.x, draftStart.y,
        draftStart.x + (dx / len) * dNorm,
        draftStart.y + (dy / len) * dNorm,
      );
      setDraftStart(null);
    } else {
      return;
    }
    setLengthInput('');
  }, [mode, polygonDraft, draftHint, draftStart, scaleMPerNorm, actions]);

  useEffect(() => {
    if (!lengthEntryActive) {
      if (lengthInputRef.current) setLengthInput('');
      return undefined;
    }
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (/^[0-9]$/.test(e.key)) {
        setLengthInput((s) => (s.length >= 6 ? s : s + e.key));
        e.preventDefault(); e.stopPropagation();
      } else if (e.key === '.' || e.key === ',') {
        setLengthInput((s) => (s.includes('.') ? s : `${s || '0'}.`));
        e.preventDefault(); e.stopPropagation();
      } else if (lengthInputRef.current && e.key === 'Backspace') {
        setLengthInput((s) => s.slice(0, -1));
        e.preventDefault(); e.stopPropagation();
      } else if (lengthInputRef.current && e.key === 'Enter') {
        commitLengthEntry();
        e.preventDefault(); e.stopPropagation();
      } else if (lengthInputRef.current && e.key === 'Escape') {
        setLengthInput('');
        e.preventDefault(); e.stopPropagation();
      }
    };
    // capture=true : passe AVANT les raccourcis globaux (Entrée = terminer
    // la polyligne) tant qu'une longueur est en cours de saisie.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [lengthEntryActive, commitLengthEntry]);

  // ---- V14.57 Sprint 12 — endpoint snap helper (used during wall draw) ----
  const snapToNearestEndpoint = useCallback((nx, ny, excludeIdx) => {
    if (!endpointSnap || !walls || walls.length === 0) return { x: nx, y: ny, snapped: false };
    const r2 = endpointSnapRadius * endpointSnapRadius;
    let best = null;
    let bestDist = r2;
    walls.forEach((w, i) => {
      if (i === excludeIdx) return;
      const ep1d = (w.x1 - nx) ** 2 + (w.y1 - ny) ** 2;
      if (ep1d < bestDist) { bestDist = ep1d; best = { x: w.x1, y: w.y1 }; }
      const ep2d = (w.x2 - nx) ** 2 + (w.y2 - ny) ** 2;
      if (ep2d < bestDist) { bestDist = ep2d; best = { x: w.x2, y: w.y2 }; }
    });
    if (best) return { x: best.x, y: best.y, snapped: true };
    return { x: nx, y: ny, snapped: false };
  }, [walls, endpointSnap, endpointSnapRadius]);

  // ---- V14.91 — Accrochage sur la FACE d'un mur existant (cloison → porteur) ----
  // Projette le curseur sur le mur le plus proche puis décale d'une demi-
  // épaisseur du mur cible : l'extrémité de la cloison vient buter contre la
  // face (pas de chevauchement, pas de jour). Côté : auto / gauche / centre / droite.
  const WALL_FACE_SNAP_RADIUS = 0.03;
  const [faceSnapInfo, setFaceSnapInfo] = useState(null);
  const snapToWallFace = useCallback((nx, ny) => {
    if (!wallFaceSnap || wallFaceSnap === 'off' || !walls || walls.length === 0) return { x: nx, y: ny, snapped: false };
    const avgDim = imgDims ? (imgDims.w + imgDims.h) / 2 : 1000;
    const r2 = WALL_FACE_SNAP_RADIUS * WALL_FACE_SNAP_RADIUS;
    let best = null;
    walls.forEach((w) => {
      const dx = w.x2 - w.x1, dy = w.y2 - w.y1;
      const len2 = dx * dx + dy * dy;
      if (len2 < 1e-9) return;
      const t = Math.max(0, Math.min(1, ((nx - w.x1) * dx + (ny - w.y1) * dy) / len2));
      const px = w.x1 + t * dx, py = w.y1 + t * dy;
      const d2 = (nx - px) ** 2 + (ny - py) ** 2;
      if (d2 < r2 && (!best || d2 < best.d2)) best = { w, px, py, d2, dx, dy, len: Math.sqrt(len2), t };
    });
    if (!best) return { x: nx, y: ny, snapped: false };
    const halfNorm = ((+best.w.thickness || 8) / 2) / avgDim;
    const nxn = -best.dy / best.len, nyn = best.dx / best.len; // normale "gauche" du sens du mur
    let side = wallFaceSnap;
    if (side === 'auto') {
      const cross = (nx - best.px) * nxn + (ny - best.py) * nyn;
      side = cross >= 0 ? 'left' : 'right';
    }
    const k = side === 'center' ? 0 : side === 'left' ? 1 : -1;
    return { x: best.px + k * halfNorm * nxn, y: best.py + k * halfNorm * nyn, snapped: true, wallId: best.w.id, side, wallType: best.w.type };
  }, [walls, wallFaceSnap, imgDims]);
  // V17.2 — similitude emprise étage (m) → plan du lot (norm) ; suit le contour (translation, échelle, rotation)
  const imgRatio = imgDims?.w && imgDims?.h ? imgDims.h / imgDims.w : 1;
  const floorFit = useMemo(() => (floorOverlay?.data?.available ? buildFloorFit({ pointsM: floorOverlay.data.points_m, outerPolygon, scale: calibration?.scale_m_per_norm_unit, ratio: imgRatio }) : null), [floorOverlay?.data, outerPolygon, calibration, imgRatio]);
  const floorImgUrl = floorOverlay?.enabled && floorOverlay.image && floorOverlay?.data?.floor_image?.url ? `${process.env.REACT_APP_BACKEND_URL}${floorOverlay.data.floor_image.url}` : '';
  const floorImg = useNativeImage(floorImgUrl);

  // Épaisseur du mur en cours de tracé (px canvas) + libellé « type · cm ».
  const draftWallStyle = useMemo(() => {
    const style = wallStyleFor(drawWallType);
    if (style.virtual) return { thicknessImgPx: 0, cm: null, color: style.color, label: `${style.label} · limite virtuelle` };
    const thPx = defaultWallThicknessPx?.(drawWallType) || 8;
    const cm = planDefaults?.walls?.[drawWallType];
    return { thicknessImgPx: thPx, cm, color: style.color, label: `${style.label}${cm ? ` · ${cm} cm` : ''}` };
  }, [drawWallType, defaultWallThicknessPx, planDefaults]);

  // ---- V14.58 Sprint 13 LOT 3 — Project (nx,ny) onto nearest wall ----
  // Returns {x, y, angle, wallId, distance} for the closest projection
  // within `menuiserieSnapRadius`, or null otherwise. `angle` is the
  // wall's orientation in radians (atan2 of dy, dx).
  const projectToNearestWall = useCallback((nx, ny) => {
    if (!walls || walls.length === 0) return null;
    const r2 = menuiserieSnapRadius * menuiserieSnapRadius;
    let best = null;
    walls.forEach((w) => {
      if (w.type === 'mur_invisible') return; // V14.96 — pas de menuiserie sur une limite virtuelle
      const dx = w.x2 - w.x1;
      const dy = w.y2 - w.y1;
      const len2 = dx * dx + dy * dy;
      if (len2 < 1e-9) return;
      // Param t of the projection on the segment, clamped to [0,1].
      const t = Math.max(0, Math.min(1, ((nx - w.x1) * dx + (ny - w.y1) * dy) / len2));
      const px = w.x1 + t * dx;
      const py = w.y1 + t * dy;
      const d2 = (nx - px) ** 2 + (ny - py) ** 2;
      if (d2 < r2 && (best === null || d2 < best.d2)) {
        best = {
          x: px, y: py,
          // ⚠ rotation stockée/rendue en DEGRÉS partout (Konva, backend).
          // V14.77 FIX : atan2 renvoie des radians → conversion obligatoire
          // (bug : fenêtres posées avec un angle de ~1.5 au lieu de 90°).
          angle: (Math.atan2(dy, dx) * 180) / Math.PI,
          wallId: w.id,
          d2,
        };
      }
    });
    if (!best) return null;
    return { ...best, distance: Math.sqrt(best.d2) };
  }, [walls, menuiserieSnapRadius]);

  // ---- V14.58 Sprint 13 — Orthogonal axis snap from a reference point ----
  // When drawing a wall with a draftStart, automatically snap the cursor
  // hint to the nearest horizontal/vertical axis if within ANGLE_TOL (5°).
  const ANGLE_TOL_DEG = 5;
  const snapToOrthogonalAxis = useCallback((nx, ny, refX, refY) => {
    if (refX === undefined || refY === undefined) return { x: nx, y: ny, snapped: false, target: null };
    const dx = nx - refX;
    const dy = ny - refY;
    if (Math.abs(dx) < 1e-9 && Math.abs(dy) < 1e-9) return { x: nx, y: ny, snapped: false, target: null };
    const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
    const a = ((angle % 180) + 180) % 180;
    const distH = Math.min(a, 180 - a);
    const distV = Math.abs(a - 90);
    if (distH <= ANGLE_TOL_DEG && distH <= distV) {
      return { x: nx, y: refY, snapped: true, target: 'h' };
    }
    if (distV <= ANGLE_TOL_DEG) {
      return { x: refX, y: ny, snapped: true, target: 'v' };
    }
    return { x: nx, y: ny, snapped: false, target: null };
  }, []);

  // ----- Resize observer for responsive canvas -----
  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver((entries) => {
      for (const e of entries) {
        const cr = e.contentRect;
        setSize({ width: Math.max(300, cr.width), height: Math.max(200, cr.height) });
      }
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // ----- Compute fit transform : map normalized [0..1] to stage pixels
  // ----- (zoom + pan via le socle commun components/project/canvas — V15.39).
  const bgDims = useMemo(() => {
    if (!bgImage) return null;
    const iw = bgImage.naturalWidth || bgImage.width, ih = bgImage.naturalHeight || bgImage.height;
    return iw && ih ? { width: iw, height: ih } : null;
  }, [bgImage]);
  const { transform, norm2px, px2norm } = useFitTransform(size, bgDims, viewport);
  const floorImgPlace = useMemo(() => {
    const c = floorOverlay?.data?.floor_image?.corners_m;
    if (!floorFit || !c || !floorImg) return null;
    const P = (m) => { const n = floorFit.toNorm(m); return norm2px(n[0], n[1]); };
    const c00 = P(c[0]), c10 = P(c[1]), c01 = P(c[3]);
    return { x: c00.x, y: c00.y, width: Math.hypot(c10.x - c00.x, c10.y - c00.y), height: Math.hypot(c01.x - c00.x, c01.y - c00.y), rotation: (Math.atan2(c10.y - c00.y, c10.x - c00.x) * 180) / Math.PI };
  }, [floorFit, floorOverlay?.data, floorImg, norm2px]);

  // ----- Zoom (molette) + pan (Ctrl/⌘ + glisser, bouton du milieu, Espace) : hook partagé -----
  const { handleWheel, handleMouseDown, handleMouseUp: handleMouseUpCanvas, zoomBy, resetView, isPanning, spaceHeld } = useCanvasViewport({ stageRef, size, viewport, setViewport });

  // ----- Pointer handlers -----
  const isPlaceMode = mode && mode.startsWith('place:');
  const placeType = isPlaceMode ? mode.split(':')[1] : null;
  const isPolygonDrawMode = mode === 'draw-room-polygon' || mode === 'draw-outer-polygon' || mode === 'draw-polyline-walls' || mode === 'draw-terrain-polygon';
  const isWallPolylineMode = mode === 'draw-polyline-walls';

  const handleStageClick = useCallback((e) => {
    if (spaceHeld || isPanning) return;  // navigation (Espace / pan) : pas d'action d'édition
    const stage = e.target.getStage();
    if (twoPoint.active) {  // V17.3 — calage 2 points : S1 (sur le contour/calque) → C1 (sur le plan), puis S2 → C2
      const pos = stage.getPointerPosition(); if (!pos) return;
      const n = px2norm(pos.x, pos.y); const pts = [...twoPoint.pts, [n.x, n.y]];
      if (pts.length < 4) { setTwoPoint({ active: true, pts }); return; }
      const T = similarityFromPairs(pts[0], pts[1], pts[2], pts[3], imgRatio);
      if (T && outerPolygon?.length >= 3) { actions.setOuterFromDetection(outerPolygon.map(T)); toast.success(`Contour calé · échelle ×${T.scale.toFixed(3)} · ${T.angleDeg.toFixed(1)}°`); }
      setTwoPoint({ active: false, pts: [] });
      return;
    }

    // Empty stage click in 'select' → deselect
    if (mode === 'select') {
      if (e.target === stage || e.target.attrs?.id === 'bg-image') {
        setSelectedWallId(null);
        setSelectedOpeningId(null);
        setSelectedRoomId(null);
        setSelectedStairsId(null);
        setSelectedSkewId(null);
        setSelectedFurnitureId(null);
      }
      return;
    }

    // V14.69 Phase 2 — Ligne de brisure (mansarde) : 2 clics (départ → fin).
    if (mode === 'draw:skew') {
      const pos = stage.getPointerPosition();
      if (!pos) return;
      const n = px2norm(pos.x, pos.y);
      let nx = Math.max(0, Math.min(1, n.x));
      let ny = Math.max(0, Math.min(1, n.y));
      if (draftStart) {
        const orth = snapToOrthogonalAxis(nx, ny, draftStart.x, draftStart.y);
        nx = orth.x; ny = orth.y;
      }
      if (!draftStart) setDraftStart({ x: nx, y: ny });
      else {
        actions.addSkewEdge(draftStart.x, draftStart.y, nx, ny);
        setDraftStart(null);
      }
      return;
    }

    // V14.52 Sprint 7 — polygon drafting (rooms or outer contour).
    // V14.58 Sprint 13 LOT 4 — also reused for wall polyline tool.
    if (isPolygonDrawMode) {
      const pos = stage.getPointerPosition();
      if (!pos) return;
      const raw = px2norm(pos.x, pos.y);
      let nx = Math.max(0, Math.min(1, raw.x));
      let ny = Math.max(0, Math.min(1, raw.y));
      // V14.53 Sprint 8 — Shift+click snaps to orthogonal (horizontal /
      // vertical) relative to the previous vertex for clean wall traces.
      if (e.evt?.shiftKey && polygonDraft.length > 0) {
        const prev = polygonDraft[polygonDraft.length - 1];
        const dx = Math.abs(nx - prev[0]);
        const dy = Math.abs(ny - prev[1]);
        if (dx > dy) ny = prev[1];
        else        nx = prev[0];
      }
      // V14.58 — endpoint snap also applies in wall polyline mode.
      if (isWallPolylineMode) {
        let snapped = endpointSnap ? snapToNearestEndpoint(nx, ny) : { snapped: false };
        if (!snapped.snapped) snapped = snapToWallFace(nx, ny);
        nx = snapped.x; ny = snapped.y;
      }
      // For ROOM and OUTER polygons, close on click near first vertex.
      if (!isWallPolylineMode && polygonDraft.length >= 3) {
        const first = polygonDraft[0];
        const fdx = first[0] - nx, fdy = first[1] - ny;
        if (Math.sqrt(fdx * fdx + fdy * fdy) < 0.02) {  // 2% of normalized image
          if (mode === 'draw-room-polygon') actions.commitRoomPolygon('autre');
          else if (mode === 'draw-outer-polygon') actions.commitOuterPolygon();
          else if (mode === 'draw-terrain-polygon') actions.commitTerrainPolygon();
          return;
        }
      }
      actions.pushPolygonDraftPoint(nx, ny);
      return;
    }

    // Sprint 26C — Stairs placement : click anywhere on the canvas to
    // place a stair rectangle at the cursor (centered). The kind is
    // picked through <StairsTypePicker> right after.
    if (mode === 'place:stairs') {
      const pos = stage.getPointerPosition();
      if (!pos) return;
      const n = px2norm(pos.x, pos.y);
      const nx = Math.max(0, Math.min(1, n.x));
      const ny = Math.max(0, Math.min(1, n.y));
      actions.beginStairsPlacement(nx, ny);
      return;
    }
    // V15.08 — Élément fixe : clic = pose d'un rectangle typé centré sur le curseur.
    if (mode.startsWith('place:fixture:')) {
      const pos = stage.getPointerPosition();
      if (!pos) return;
      const n = px2norm(pos.x, pos.y);
      actions.addFixture(mode.slice('place:fixture:'.length), Math.max(0, Math.min(1, n.x)), Math.max(0, Math.min(1, n.y)));
      return;
    }
    // V15.32 — Mobilier : clic = pose d'un meuble à ses dimensions réelles, centré sur le curseur.
    if (mode.startsWith('place:furniture:')) {
      const pos = stage.getPointerPosition();
      if (!pos) return;
      const n = px2norm(pos.x, pos.y);
      actions.addFurniture(mode.slice('place:furniture:'.length), Math.max(0, Math.min(1, n.x)), Math.max(0, Math.min(1, n.y)));
      return;
    }

    // Openings sub-tab : place a new menuiserie
    // V14.58 Sprint 13 LOT 3 — when snap ON, project to nearest wall and
    // rotate menuiserie to wall angle. Refuse placement if no wall within
    // radius. Snap can be turned off in OpeningsToolbar.
    if (activeSubTab === 'openings' && isPlaceMode) {
      const pos = stage.getPointerPosition();
      if (!pos) return;
      const n = px2norm(pos.x, pos.y);
      let nx = Math.max(0, Math.min(1, n.x));
      let ny = Math.max(0, Math.min(1, n.y));
      let rotation = 0;
      let wallId = null;
      if (menuiserieSnap) {
        const projected = projectToNearestWall(nx, ny);
        if (!projected) {
          toast.error("Aucun mur à proximité — désactivez le snap pour placer librement.");
          return;
        }
        nx = projected.x; ny = projected.y;
        rotation = projected.angle;
        wallId = projected.wallId;
      }
      actions.addOpening(placeType, nx, ny, { rotation, wall_id: wallId });
      return;
    }

    // Walls sub-tab : 'draw' (line) mode click-click
    if (activeSubTab === 'walls' && mode === 'draw') {
      const pos = stage.getPointerPosition();
      if (!pos) return;
      const n = px2norm(pos.x, pos.y);
      let nx = Math.max(0, Math.min(1, n.x));
      let ny = Math.max(0, Math.min(1, n.y));
      // V14.58 — also snap to orthogonal axis from draftStart (5° tol).
      if (draftStart) {
        const orth = snapToOrthogonalAxis(nx, ny, draftStart.x, draftStart.y);
        nx = orth.x; ny = orth.y;
      }
      // V14.57 Sprint 12 — snap to nearest existing endpoint when ON.
      let snapped = snapToNearestEndpoint(nx, ny);
      // V14.91 — sinon accrochage sur la face du mur le plus proche.
      if (!snapped.snapped) snapped = snapToWallFace(nx, ny);
      nx = snapped.x; ny = snapped.y;
      if (!draftStart) setDraftStart({ x: nx, y: ny });
      else { actions.addWall(draftStart.x, draftStart.y, nx, ny); setDraftStart(null); }
    }
  }, [spaceHeld, isPanning, twoPoint, setTwoPoint, imgRatio, outerPolygon, mode, activeSubTab, isPlaceMode, placeType, isPolygonDrawMode, isWallPolylineMode,
      polygonDraft, draftStart, actions, px2norm,
      snapToNearestEndpoint, snapToOrthogonalAxis, snapToWallFace,
      projectToNearestWall, menuiserieSnap, endpointSnap,
      setSelectedWallId, setSelectedOpeningId, setSelectedRoomId,
      setSelectedStairsId, setSelectedSkewId, setSelectedFurnitureId]);

  // Double-click finalizes a polygon (≥3 vertices for rooms/outer, ≥2
  // vertices for the wall polyline tool) without needing to click back.
  const handleStageDblClick = useCallback(() => {
    if (!isPolygonDrawMode) return;
    if (mode === 'draw-room-polygon' && polygonDraft.length >= 3) actions.commitRoomPolygon('autre');
    else if (mode === 'draw-outer-polygon' && polygonDraft.length >= 3) actions.commitOuterPolygon();
    else if (mode === 'draw-terrain-polygon' && polygonDraft.length >= 3) actions.commitTerrainPolygon();
    else if (mode === 'draw-polyline-walls' && polygonDraft.length >= 2) actions.commitWallPolyline(drawWallType);
  }, [isPolygonDrawMode, polygonDraft.length, mode, actions, drawWallType]);

  const handleStageMouseMove = useCallback((e) => {
    if (isPolygonDrawMode) {
      const pos = e.target.getStage().getPointerPosition();
      if (!pos) return;
      const n = px2norm(pos.x, pos.y);
      let hx = n.x, hy = n.y;
      // V14.53 Sprint 8 — Shift snaps the live hint to orthogonal relative
      // to the last placed vertex so the user previews the snapped segment.
      if (e.evt?.shiftKey && polygonDraft.length > 0) {
        const prev = polygonDraft[polygonDraft.length - 1];
        if (Math.abs(hx - prev[0]) > Math.abs(hy - prev[1])) hy = prev[1];
        else hx = prev[0];
      }
      if (isWallPolylineMode) {
        let sn = endpointSnap ? snapToNearestEndpoint(hx, hy) : { snapped: false };
        if (!sn.snapped) sn = snapToWallFace(hx, hy);
        hx = sn.x; hy = sn.y;
        setFaceSnapInfo(sn.snapped && sn.wallId ? sn : null);
      }
      setDraftHint([hx, hy]);
      return;
    }
    if (activeSubTab !== 'walls' || mode !== 'draw') { if (faceSnapInfo) setFaceSnapInfo(null); return; }
    const pos = e.target.getStage().getPointerPosition();
    if (!pos) return;
    const n = px2norm(pos.x, pos.y);
    let hx = n.x, hy = n.y;
    if (draftStart) {
      const orth = snapToOrthogonalAxis(hx, hy, draftStart.x, draftStart.y);
      hx = orth.x; hy = orth.y;
    }
    let sn = snapToNearestEndpoint(hx, hy);
    if (!sn.snapped) sn = snapToWallFace(hx, hy);
    setFaceSnapInfo(sn.snapped && sn.wallId ? { ...sn } : null);
    if (!draftStart) { setHoverHint({ x: sn.x, y: sn.y }); return; }
    setDraftStart((prev) => prev ? { ...prev, _hint: { x: sn.x, y: sn.y } } : prev);
  }, [mode, activeSubTab, isPolygonDrawMode, isWallPolylineMode, draftStart, polygonDraft, px2norm, setDraftHint,
      endpointSnap, snapToNearestEndpoint, snapToWallFace, snapToOrthogonalAxis, faceSnapInfo]);
  const [hoverHint, setHoverHint] = useState(null);

  const handleWallClick = useCallback((wallId) => {
    if (activeSubTab !== 'walls') return;  // walls are read-only outside their tab
    if (mode === 'erase') { actions.deleteWall(wallId); return; }
    setSelectedWallId(wallId);
  }, [mode, activeSubTab, actions, setSelectedWallId]);

  const handleOpeningClick = useCallback((openingId) => {
    if (activeSubTab !== 'openings') return;
    if (mode === 'erase') { actions.deleteOpening(openingId); return; }
    setSelectedOpeningId(openingId);
  }, [mode, activeSubTab, actions, setSelectedOpeningId]);

  const handleRoomClick = useCallback((roomId) => {
    if (activeSubTab !== 'rooms') return;
    if (mode === 'erase') { actions.deleteRoom(roomId); return; }
    setSelectedRoomId(roomId);
  }, [mode, activeSubTab, actions, setSelectedRoomId]);

  const handleOpeningDragEnd = useCallback((id, e) => {
    if (activeSubTab !== 'openings') return;
    const node = e.target;
    const n = px2norm(node.x(), node.y());
    let nx = Math.max(0, Math.min(1, n.x));
    let ny = Math.max(0, Math.min(1, n.y));
    // V14.59 Sprint 14 — apply the same wall-snap as at placement.
    // When snap ON and a wall is in range, the menuiserie sticks to the
    // wall and its rotation is updated to the wall angle. When snap OFF
    // or no wall in range, free placement is allowed.
    if (menuiserieSnap) {
      const projected = projectToNearestWall(nx, ny);
      if (projected) {
        actions.updateOpening(id, {
          x: projected.x, y: projected.y,
          rotation: projected.angle,
          wall_id: projected.wallId,
        });
        // Sync the Konva node position to the snapped point so the
        // visual matches the persisted state (otherwise it keeps the
        // unsnapped drop position until the next render).
        const snappedPx = norm2px(projected.x, projected.y);
        node.position({ x: snappedPx.x, y: snappedPx.y });
        return;
      }
    }
    actions.updateOpening(id, { x: nx, y: ny });
  }, [activeSubTab, actions, px2norm, norm2px,
      menuiserieSnap, projectToNearestWall]);

  // V14.57 Sprint 12 — compute endpoint clusters (same shape used by
  // the sidebar panel). Rendered as small dots in the canvas so the
  // user sees joined corners at a glance.
  const endpointClustersForCanvas = useMemo(() => {
    if (activeSubTab !== 'walls' || !walls || walls.length === 0) return [];
    const eps = [];
    walls.forEach((w) => {
      eps.push({ x: w.x1, y: w.y1 });
      eps.push({ x: w.x2, y: w.y2 });
    });
    const r2 = endpointSnapRadius * endpointSnapRadius;
    const parent = eps.map((_, i) => i);
    const find = (a) => { while (parent[a] !== a) { parent[a] = parent[parent[a]]; a = parent[a]; } return a; };
    for (let i = 0; i < eps.length; i += 1) {
      for (let j = i + 1; j < eps.length; j += 1) {
        const dx = eps[i].x - eps[j].x; const dy = eps[i].y - eps[j].y;
        if (dx * dx + dy * dy <= r2) {
          const ri = find(i); const rj = find(j); if (ri !== rj) parent[ri] = rj;
        }
      }
    }
    const groups = new Map();
    eps.forEach((ep, i) => {
      const r = find(i); if (!groups.has(r)) groups.set(r, []); groups.get(r).push(ep);
    });
    return Array.from(groups.values()).map((group) => {
      const cx = group.reduce((s, e) => s + e.x, 0) / group.length;
      const cy = group.reduce((s, e) => s + e.y, 0) / group.length;
      return { cx, cy, count: group.length };
    });
  }, [walls, endpointSnapRadius, activeSubTab]);

  // ----- Cursor by mode -----
  let cursor = 'default';
  if (isPanning) cursor = 'grabbing';
  else if (spaceHeld) cursor = 'grab';
  else if (activeSubTab === 'walls') {
    if (mode === 'draw' || mode === 'draw-polyline-walls') cursor = 'crosshair';
    else if (mode === 'erase') cursor = 'not-allowed';
  } else if (activeSubTab === 'openings') {
    if (isPlaceMode) cursor = 'copy';
    else if (mode === 'erase') cursor = 'not-allowed';
  } else if (activeSubTab === 'furniture') {
    cursor = mode.startsWith('place:furniture:') ? 'copy' : 'pointer';
  } else if (activeSubTab === 'rooms') {
    if (mode === 'erase') cursor = 'not-allowed';
    else if (mode === 'draw-room-polygon') cursor = 'crosshair';
    else cursor = 'pointer';
  } else if (activeSubTab === 'contour') {
    if (mode === 'draw-outer-polygon') cursor = 'crosshair';
    if (mode === 'draw-terrain-polygon') cursor = 'crosshair';
  }

  return (
    <div ref={containerRef} className="w-full h-full relative bg-black/20" style={{ cursor }}>
      <Stage
        ref={(node) => {
          stageRef.current = node;
          if (ctxStageRef) ctxStageRef.current = node;
        }}
        width={size.width}
        height={size.height}
        onClick={handleStageClick}
        onTap={handleStageClick}
        onMouseMove={handleStageMouseMove}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUpCanvas}
        onWheel={handleWheel}
        onDblClick={handleStageDblClick}
        onDblTap={handleStageDblClick}
      >
        <Layer listening={activeSubTab === 'contour' && mode === 'select' && !twoPoint.active}>
          {bgImage && (
            <KonvaImage
              id="bg-image"
              image={bgImage}
              x={transform.offsetX}
              y={transform.offsetY}
              width={transform.imgW}
              height={transform.imgH}
              opacity={bgOpacity}
              listening={false}
            />
          )}
          {outerPolygon && outerPolygon.length >= 2 && (
            <Line
              points={outerPolygon.flatMap((p) => {
                const pt = norm2px(p[0], p[1]);
                return [pt.x, pt.y];
              })}
              stroke="#f59e0b"
              strokeWidth={2.5}
              closed={outerPolygon.length >= 3}
              listening={activeSubTab === 'contour' && mode === 'select'}
              draggable={activeSubTab === 'contour' && mode === 'select'}
              fill={activeSubTab === 'contour' && mode === 'select' ? 'rgba(245,158,11,0.06)' : undefined}
              onMouseEnter={(e) => { if (activeSubTab === 'contour' && mode === 'select') e.target.getStage().container().style.cursor = 'move'; }}
              onMouseLeave={(e) => { e.target.getStage().container().style.cursor = ''; }}
              onDragEnd={(e) => {  // V17.1 — déplacer le contour entier (calage sur le plan)
                const dx = e.target.x() / Math.max(1, transform.scaleX), dy = e.target.y() / Math.max(1, transform.scaleY);
                e.target.position({ x: 0, y: 0 });
                actions.setOuterFromDetection(outerPolygon.map((p) => [Math.min(1, Math.max(0, p[0] + dx)), Math.min(1, Math.max(0, p[1] + dy))]));
              }}
              dash={[6, 4]}
            />
          )}
          {activeSubTab === 'contour' && mode === 'select' && outerPolygon && outerPolygon.length >= 3 && outerPolygon.map((p, i) => {
            const pt = norm2px(p[0], p[1]);
            return (
              <Circle key={`ov_${i}`} x={pt.x} y={pt.y} radius={5} fill="#fff" stroke="#f59e0b" strokeWidth={2} draggable
                onMouseEnter={(e) => { e.target.getStage().container().style.cursor = 'grab'; }}
                onMouseLeave={(e) => { e.target.getStage().container().style.cursor = ''; }}
                onDragEnd={(e) => { const n = px2norm(e.target.x(), e.target.y()); actions.setOuterFromDetection(outerPolygon.map((q, j) => (j === i ? [Math.min(1, Math.max(0, n.x)), Math.min(1, Math.max(0, n.y))] : q))); }}
                data-testid={`contour-vertex-${i}`} />
            );
          })}
          {activeSubTab === 'contour' && mode === 'select' && outerPolygon && outerPolygon.length >= 3 && (() => {  // V17.2 — poignée de rotation fine
            const c = outerPolygon.reduce((a, p) => [a[0] + p[0] / outerPolygon.length, a[1] + p[1] / outerPolygon.length], [0, 0]);
            const cpx = norm2px(c[0], c[1]); const top = Math.min(...outerPolygon.map((p) => norm2px(p[0], p[1]).y)) - 28;
            return (
              <React.Fragment>
                <Line points={[cpx.x, cpx.y, cpx.x, top]} stroke="#f59e0b" strokeWidth={1} dash={[3, 3]} />
                <Circle x={cpx.x} y={top} radius={7} fill="#f59e0b" stroke="#fff" strokeWidth={2} draggable
                  onMouseEnter={(e) => { e.target.getStage().container().style.cursor = 'crosshair'; }}
                  onMouseLeave={(e) => { e.target.getStage().container().style.cursor = ''; }}
                  onDragMove={(e) => { e.target.y(e.target.y()); }}
                  onDragEnd={(e) => {
                    const ang = Math.atan2(e.target.y() - cpx.y, e.target.x() - cpx.x) - Math.atan2(top - cpx.y, 0);
                    e.target.position({ x: cpx.x, y: top });
                    actions.setOuterFromDetection(rotatePolygon(outerPolygon, (ang * 180) / Math.PI, imgRatio));
                  }}
                  data-testid="contour-rotate-handle" />
              </React.Fragment>
            );
          })()}
          {/* Sprint 26D — Terrain polygon (property boundary).
              Rendered with a sand-yellow dashed stroke + a translucent
              green fill so it reads as "plot / ground" rather than wall.
              Drawn below the building outer so the contour stays
              visible on top. */}
          {terrainPolygon && terrainPolygon.length >= 2 && (
            <Line
              points={terrainPolygon.flatMap((p) => {
                const pt = norm2px(p[0], p[1]);
                return [pt.x, pt.y];
              })}
              stroke="#fbbf24"
              strokeWidth={2.5}
              closed={terrainPolygon.length >= 3}
              fill={terrainPolygon.length >= 3 ? 'rgba(34,197,94,0.10)' : undefined}
              listening={false}
              dash={[10, 5]}
            />
          )}
        </Layer>
        {/* V17.2 — calque d'étage (enveloppe, murs séparatifs, lots voisins, parties communes) */}
        {floorFit && floorOverlay.enabled && (
          <Layer listening={false} opacity={floorOverlay.opacity} data-testid="floor-overlay-layer">
            {floorImgPlace && <KonvaImage image={floorImg} {...floorImgPlace} opacity={floorOverlay.imageOpacity / Math.max(0.05, floorOverlay.opacity)} />}
            {floorOverlay.envelope && floorOverlay.data.floor_envelope_m && (
              <Line points={floorOverlay.data.floor_envelope_m.flatMap((m) => { const n = floorFit.toNorm(m); const pt = norm2px(n[0], n[1]); return [pt.x, pt.y]; })} closed stroke="#0ea5e9" strokeWidth={2} dash={[10, 6]} />
            )}
            {floorOverlay.commons && (floorOverlay.data.floor_commons_m || []).map((c) => {
              const pts = c.points_m.map((m) => { const n = floorFit.toNorm(m); return norm2px(n[0], n[1]); });
              const cx = pts.reduce((a, p) => a + p.x / pts.length, 0); const cy = pts.reduce((a, p) => a + p.y / pts.length, 0);
              return <React.Fragment key={`fc_${c.id}`}><Line points={pts.flatMap((p) => [p.x, p.y])} closed stroke="#a78bfa" strokeWidth={1.5} fill="rgba(167,139,250,0.12)" /><Text x={cx - 40} y={cy - 6} width={80} align="center" text={c.label} fontSize={10} fill="#6d28d9" /></React.Fragment>;
            })}
            {floorOverlay.lots && (floorOverlay.data.floor_lots_m || []).map((l) => {
              const pts = l.points_m.map((m) => { const n = floorFit.toNorm(m); return norm2px(n[0], n[1]); });
              const cx = pts.reduce((a, p) => a + p.x / pts.length, 0); const cy = pts.reduce((a, p) => a + p.y / pts.length, 0);
              return <React.Fragment key={`fl_${l.id}`}><Line points={pts.flatMap((p) => [p.x, p.y])} closed stroke="#38bdf8" strokeWidth={1.5} fill="rgba(56,189,248,0.10)" /><Text x={cx - 40} y={cy - 6} width={80} align="center" text={l.label} fontSize={10} fill="#0369a1" /></React.Fragment>;
            })}
            {floorOverlay.walls && (floorOverlay.data.floor_walls_m || []).map((w) => (
              <Line key={`fw_${w.id}`} points={w.points_m.flatMap((m) => { const n = floorFit.toNorm(m); const pt = norm2px(n[0], n[1]); return [pt.x, pt.y]; })} stroke="#475569" strokeWidth={Math.max(3, (w.thickness_m || 0.2) * floorFit.scaleNormPerM * transform.scaleX)} lineCap="square" />
            ))}
          </Layer>
        )}

        {/* WALLS LAYER */}
        {twoPoint.active && (
          <Layer listening={false}>
            {twoPoint.pts.map((p, i) => { const pt = norm2px(p[0], p[1]); const src = i % 2 === 0; return (
              <React.Fragment key={`tp_${i}`}>
                <Circle x={pt.x} y={pt.y} radius={7} stroke={src ? '#f59e0b' : '#16a34a'} strokeWidth={2.5} fill="rgba(255,255,255,0.7)" />
                <Text x={pt.x + 9} y={pt.y - 7} text={`${src ? 'S' : 'C'}${Math.floor(i / 2) + 1}`} fontSize={11} fontStyle="bold" fill={src ? '#b45309' : '#15803d'} />
              </React.Fragment>); })}
            {twoPoint.pts.length >= 2 && [0, 2].map((i) => twoPoint.pts[i + 1] && (() => { const a = norm2px(twoPoint.pts[i][0], twoPoint.pts[i][1]); const b = norm2px(twoPoint.pts[i + 1][0], twoPoint.pts[i + 1][1]); return <Arrow key={`tpa_${i}`} points={[a.x, a.y, b.x, b.y]} stroke="#64748b" fill="#64748b" strokeWidth={1.5} dash={[4, 3]} pointerLength={6} pointerWidth={6} />; })())}
          </Layer>
        )}
        <Layer listening={activeSubTab === 'walls'}>
          {walls.map((w) => {
            const style = wallStyleFor(w.type);
            const isSel = w.id === selectedWallId;
            const isHov = w.id === hoveredWallId;
            const p1 = norm2px(w.x1, w.y1);
            const p2 = norm2px(w.x2, w.y2);
            const strokeWidth = (isSel ? style.strokeWidth + 3 : style.strokeWidth) * (isHov ? 1.6 : 1);
            // V14.79 — cotation : longueur réelle du mur (m) sur plan calibré.
            let dim = null;
            if (showDimensions && scaleMPerNorm && activeSubTab === 'walls') {
              const lenM = Math.hypot(w.x2 - w.x1, w.y2 - w.y1) * scaleMPerNorm;
              if (lenM >= 0.15) {
                const dxp = p2.x - p1.x;
                const dyp = p2.y - p1.y;
                const lenPx = Math.hypot(dxp, dyp) || 1;
                let angle = (Math.atan2(dyp, dxp) * 180) / Math.PI;
                let px = -dyp / lenPx;
                let py = dxp / lenPx;
                if (angle > 90 || angle < -90) { angle += 180; px = -px; py = -py; }
                dim = {
                  x: (p1.x + p2.x) / 2 + px * 9,
                  y: (p1.y + p2.y) / 2 + py * 9,
                  angle,
                  text: `${lenM.toFixed(2)} m`,
                };
              }
            }
            return (
              <React.Fragment key={w.id}>
              <Line
                points={[p1.x, p1.y, p2.x, p2.y]}
                stroke={style.color}
                strokeWidth={strokeWidth}
                dash={style.dashed ? [8, 6] : undefined}
                shadowColor={isSel || isHov ? style.color : undefined}
                shadowBlur={isSel || isHov ? 8 : 0}
                opacity={activeSubTab === 'walls' ? (isHov ? 1 : 0.95) : 0.55}
                onClick={() => handleWallClick(w.id)}
                onTap={() => handleWallClick(w.id)}
                onMouseEnter={() => setHoveredWallId(w.id)}
                onMouseLeave={() => setHoveredWallId(null)}
                hitStrokeWidth={Math.max(10, strokeWidth + 8)}
              />
              {dim && (
                <Text
                  x={dim.x}
                  y={dim.y}
                  offsetX={dim.text.length * 2.55}
                  offsetY={4.5}
                  rotation={dim.angle}
                  text={dim.text}
                  fontSize={9}
                  fontStyle={isSel || isHov ? 'bold' : 'normal'}
                  fill={isSel || isHov ? '#ffffff' : 'rgba(255,255,255,0.85)'}
                  shadowColor="rgba(0,0,0,0.95)"
                  shadowBlur={3}
                  listening={false}
                />
              )}
              </React.Fragment>
            );
          })}

          {activeSubTab === 'walls' && mode === 'draw' && draftStart && (
            <>
              {/* V14.58 Sprint 12.5 — Orthogonal guides : H/V dashed
                  lines through draftStart spanning the full canvas. */}
              <Line
                points={[transform.offsetX, norm2px(draftStart.x, draftStart.y).y,
                         transform.offsetX + transform.imgW, norm2px(draftStart.x, draftStart.y).y]}
                stroke="#fbbf24" strokeWidth={1} dash={[3, 5]} opacity={0.55} listening={false}
              />
              <Line
                points={[norm2px(draftStart.x, draftStart.y).x, transform.offsetY,
                         norm2px(draftStart.x, draftStart.y).x, transform.offsetY + transform.imgH]}
                stroke="#fbbf24" strokeWidth={1} dash={[3, 5]} opacity={0.55} listening={false}
              />
              <Circle
                x={norm2px(draftStart.x, draftStart.y).x}
                y={norm2px(draftStart.x, draftStart.y).y}
                radius={4}
                fill="#ef4444"
              />
              {draftStart._hint && (() => {
                const a = norm2px(draftStart.x, draftStart.y);
                const b = norm2px(draftStart._hint.x, draftStart._hint.y);
                const thPx = draftWallStyle.thicknessImgPx ? Math.max(2, draftWallStyle.thicknessImgPx * (transform.imgW / (imgDims?.w || transform.imgW))) : 0;
                return (
                  <>
                    {/* V14.91 — épaisseur réelle du type de mur en cours de tracé */}
                    {thPx > 0 && <Line points={[a.x, a.y, b.x, b.y]} stroke={draftWallStyle.color} strokeWidth={thPx} opacity={0.35} lineCap="butt" listening={false} />}
                    <Line points={[a.x, a.y, b.x, b.y]} stroke={draftWallStyle.color} strokeWidth={1.5} dash={[4, 4]} listening={false} />
                    <Text x={b.x + 10} y={b.y - 22} text={draftWallStyle.label} fontSize={11} fill="#fff" padding={3}
                          fontStyle="bold" listening={false} shadowColor="#000" shadowBlur={4} />
                  </>
                );
              })()}
            </>
          )}
          {/* V14.91 — indicateur d'accrochage sur face de mur (cloison → porteur) */}
          {activeSubTab === 'walls' && (mode === 'draw' || mode === 'draw-polyline-walls') && faceSnapInfo && (() => {
            const p = norm2px(faceSnapInfo.x, faceSnapInfo.y);
            const sideLabel = faceSnapInfo.side === 'center' ? 'axe' : faceSnapInfo.side === 'left' ? 'face gauche' : 'face droite';
            return (
              <>
                <Circle x={p.x} y={p.y} radius={7} stroke="#22d3ee" strokeWidth={2} fill="rgba(34,211,238,0.25)" listening={false} />
                <Text x={p.x + 10} y={p.y + 6} text={`↳ ${wallStyleFor(faceSnapInfo.wallType).label} · ${sideLabel}`} fontSize={10}
                      fill="#22d3ee" listening={false} shadowColor="#000" shadowBlur={4} />
              </>
            );
          })()}
          {activeSubTab === 'walls' && mode === 'draw' && !draftStart && hoverHint && (
            <Circle x={norm2px(hoverHint.x, hoverHint.y).x} y={norm2px(hoverHint.x, hoverHint.y).y} radius={3} fill={draftWallStyle.color} listening={false} />
          )}

          {/* V14.58 Sprint 13 LOT 4 — wall polyline draft preview is
              handled by the shared POLYGON DRAFT LAYER below (covers
              all isPolygonDrawMode variants). */}

          {/* V14.57 Sprint 12 — Endpoint cluster markers (small dots). */}
          {endpointClustersForCanvas.map((c, i) => {
            const p = norm2px(c.cx, c.cy);
            const joined = c.count >= 2;
            return (
              <Circle
                key={`ep-${i}`}
                x={p.x}
                y={p.y}
                radius={joined ? 5 : 3}
                stroke={joined ? '#10b981' : '#fbbf24'}
                strokeWidth={joined ? 2 : 1.2}
                fill={joined ? 'rgba(16,185,129,0.25)' : 'rgba(251,191,36,0.15)'}
                listening={false}
              />
            );
          })}
        </Layer>

        {/* GHOST LAYER — V14.72 superposition étages (onion skin). Murs +
            contours des autres niveaux en translucide, non interactif. */}
        {(ghostStructures || []).length > 0 && (
          <Layer listening={false}>
            {ghostStructures.map((g) => (
              <React.Fragment key={`ghost-${g.lot_id}`}>
                {(g.outer_polygon || []).length >= 3 && (
                  <Line
                    points={g.outer_polygon.flatMap((p) => {
                      const px = norm2px(p[0] ?? p.x, p[1] ?? p.y);
                      return [px.x, px.y];
                    })}
                    closed
                    stroke={g.color}
                    strokeWidth={2.5}
                    dash={[14, 7]}
                    opacity={0.55}
                  />
                )}
                {(g.walls || []).map((w) => {
                  const p1 = norm2px(w.x1, w.y1);
                  const p2 = norm2px(w.x2, w.y2);
                  return (
                    <Line
                      key={`ghost-${g.lot_id}-${w.id}`}
                      points={[p1.x, p1.y, p2.x, p2.y]}
                      stroke={g.color}
                      strokeWidth={w.type === 'porteur' ? 5 : 2.5}
                      opacity={0.35}
                      lineCap="round"
                    />
                  );
                })}
              </React.Fragment>
            ))}
          </Layer>
        )}

        {/* STAIRS LAYER — Sprint 26C + V14.69 Phase 1 : draggable,
            redimensionnable (poignée coin bas-droit) et flèches de
            direction (monte ↑ / descend ↓ / les deux ↕). Les copies
            miroir (source='mirror') venant d'un étage lié sont grisées. */}
        <Layer listening={activeSubTab === 'walls'}>
          {(stairs || []).map((st) => {
            const isSel = st.id === selectedStairsId;
            const isMirror = st.source === 'mirror';
            const tl = norm2px(st.x, st.y);
            const br = norm2px(st.x + st.w, st.y + st.h);
            const w = br.x - tl.x;
            const h = br.y - tl.y;
            const cx = tl.x + w / 2;
            const dir = st.direction || 'up';
            const baseColor = isMirror ? '#94a3b8' : '#a855f7';
            const arrows = [];
            if (dir === 'up' || dir === 'both') {
              arrows.push([cx - (dir === 'both' ? w * 0.15 : 0), tl.y + h * 0.78, cx - (dir === 'both' ? w * 0.15 : 0), tl.y + h * 0.22]);
            }
            if (dir === 'down' || dir === 'both') {
              arrows.push([cx + (dir === 'both' ? w * 0.15 : 0), tl.y + h * 0.22, cx + (dir === 'both' ? w * 0.15 : 0), tl.y + h * 0.78]);
            }
            return (
              <React.Fragment key={st.id}>
                <Rect
                  id={`stair-${st.id}`}
                  x={tl.x} y={tl.y} width={w} height={h}
                  fill={isSel ? 'rgba(168,85,247,0.28)' : isMirror ? 'rgba(148,163,184,0.10)' : 'rgba(168,85,247,0.14)'}
                  stroke={isSel ? '#c084fc' : baseColor}
                  strokeWidth={isSel ? 2.5 : 1.4}
                  dash={[6, 3]}
                  cornerRadius={3}
                  shadowColor={baseColor}
                  shadowBlur={isSel ? 10 : 0}
                  shadowOpacity={0.6}
                  draggable={activeSubTab === 'walls' && mode === 'select'}
                  onDragEnd={(e) => {
                    const node = e.target;
                    const n = px2norm(node.x(), node.y());
                    actions.updateStairs(st.id, {
                      x: Math.max(0, Math.min(1 - st.w, n.x)),
                      y: Math.max(0, Math.min(1 - st.h, n.y)),
                    });
                  }}
                  onClick={() => setSelectedStairsId(st.id)}
                  onTap={() => setSelectedStairsId(st.id)}
                />
                {arrows.map((pts, i) => (
                  <Arrow
                    key={`${st.id}-arrow-${i}`}
                    points={pts}
                    stroke={isSel ? '#e9d5ff' : baseColor}
                    fill={isSel ? '#e9d5ff' : baseColor}
                    strokeWidth={2}
                    pointerLength={Math.min(8, h * 0.18)}
                    pointerWidth={Math.min(7, w * 0.28)}
                    listening={false}
                  />
                ))}
                {isSel && (
                  <Circle
                    x={br.x} y={br.y} radius={6}
                    fill="#c084fc" stroke="#fff" strokeWidth={1.5}
                    draggable
                    onDragEnd={(e) => {
                      const n = px2norm(e.target.x(), e.target.y());
                      const nw = Math.max(0.02, Math.min(1 - st.x, n.x - st.x));
                      const nh = Math.max(0.02, Math.min(1 - st.y, n.y - st.y));
                      actions.updateStairs(st.id, { w: nw, h: nh });
                    }}
                    data-testid={`stair-resize-${st.id}`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Layer>

        {/* FIXTURES LAYER — V15.08 : éléments fixes typés (îlot, cheminée, colonne, placard, douche…),
            draggables + poignée de redimensionnement, extrudés dans la maquette blanche. */}
        <Layer listening={activeSubTab === 'walls'}>
          {(fixtures || []).map((fx) => {
            const isSel = fx.id === selectedFixtureId;
            const k = fixtureKindFor(fx.kind);
            const tl = norm2px(fx.x, fx.y);
            const br = norm2px(fx.x + fx.w, fx.y + fx.h);
            const w = br.x - tl.x;
            const h = br.y - tl.y;
            const rot = +fx.rotation_deg || 0;
            const ctr = { x: tl.x + w / 2, y: tl.y + h / 2 };
            const rad = (rot * Math.PI) / 180;
            // coin bas-droit après rotation autour du centre
            const hx = ctr.x + (w / 2) * Math.cos(rad) - (h / 2) * Math.sin(rad);
            const hy = ctr.y + (w / 2) * Math.sin(rad) + (h / 2) * Math.cos(rad);
            return (
              <React.Fragment key={fx.id}>
                <Rect
                  id={`fixture-${fx.id}`}
                  x={ctr.x} y={ctr.y} offsetX={w / 2} offsetY={h / 2} rotation={rot} width={w} height={h}
                  fill={isSel ? `${k.color}55` : `${k.color}2e`}
                  stroke={k.color}
                  strokeWidth={isSel ? 2.5 : 1.4}
                  cornerRadius={fx.kind === 'colonne' ? Math.min(w, h) / 2 : 2}
                  draggable={activeSubTab === 'walls' && mode === 'select'}
                  onDragEnd={(e) => {
                    const n = px2norm(e.target.x() - w / 2, e.target.y() - h / 2);
                    actions.updateFixture(fx.id, { x: Math.max(0, Math.min(1 - fx.w, n.x)), y: Math.max(0, Math.min(1 - fx.h, n.y)) });
                  }}
                  onClick={() => setSelectedFixtureId(fx.id)}
                  onTap={() => setSelectedFixtureId(fx.id)}
                  data-testid={`fixture-${fx.id}`}
                />
                {w > 28 && (
                  <Text x={ctr.x} y={ctr.y} offsetX={w / 2 - 2} offsetY={h / 2 - 2} rotation={rot} width={Math.max(10, w - 4)} text={k.label} fontSize={Math.max(8, Math.min(11, h * 0.45))}
                        fill="#fff" listening={false} ellipsis wrap="none" />
                )}
                {isSel && (
                  <Circle
                    x={hx} y={hy} radius={6}
                    fill={k.color} stroke="#fff" strokeWidth={1.5}
                    draggable
                    onDragEnd={(e) => {
                      // vecteur centre→poignée exprimé dans le repère local (dé-rotation)
                      const dx = e.target.x() - ctr.x, dy = e.target.y() - ctr.y;
                      const lx = dx * Math.cos(-rad) - dy * Math.sin(-rad);
                      const ly = dx * Math.sin(-rad) + dy * Math.cos(-rad);
                      const nw = Math.max(0.01, px2norm(tl.x + Math.max(8, lx * 2), tl.y).x - fx.x);
                      const nh = Math.max(0.01, px2norm(tl.x, tl.y + Math.max(8, ly * 2)).y - fx.y);
                      actions.updateFixture(fx.id, { w: Math.min(1 - fx.x, nw), h: Math.min(1 - fx.y, nh) });
                    }}
                    data-testid={`fixture-resize-${fx.id}`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Layer>

        {/* FURNITURE LAYER — V15.32 : mobilier verrouillé (gabarits aux dimensions réelles),
            interactif dans l'onglet 4 · Mobilier, visible en filigrane ailleurs. */}
        <Layer listening={activeSubTab === 'furniture'} opacity={activeSubTab === 'furniture' ? 1 : 0.35}>
          {(furniture || []).map((fn) => {
            const isSel = fn.id === selectedFurnitureId;
            const k = furnitureKindFor(fn.kind);
            const tl = norm2px(fn.x, fn.y);
            const br = norm2px(fn.x + fn.w, fn.y + fn.h);
            const w = br.x - tl.x;
            const h = br.y - tl.y;
            const rot = +fn.rotation_deg || 0;
            const ctr = { x: tl.x + w / 2, y: tl.y + h / 2 };
            const rad = (rot * Math.PI) / 180;
            const hx = ctr.x + (w / 2) * Math.cos(rad) - (h / 2) * Math.sin(rad);
            const hy = ctr.y + (w / 2) * Math.sin(rad) + (h / 2) * Math.cos(rad);
            return (
              <React.Fragment key={fn.id}>
                <Rect
                  id={`furniture-${fn.id}`}
                  x={ctr.x} y={ctr.y} offsetX={w / 2} offsetY={h / 2} rotation={rot} width={w} height={h}
                  fill={isSel ? `${k.color}77` : `${k.color}44`}
                  stroke={isSel ? '#fff' : k.color}
                  strokeWidth={isSel ? 2 : 1.2}
                  cornerRadius={3}
                  draggable={activeSubTab === 'furniture' && mode === 'select'}
                  onDragEnd={(e) => {
                    const n = px2norm(e.target.x() - w / 2, e.target.y() - h / 2);
                    actions.updateFurniture(fn.id, { x: Math.max(0, Math.min(1 - fn.w, n.x)), y: Math.max(0, Math.min(1 - fn.h, n.y)) });
                  }}
                  onClick={(e) => { e.cancelBubble = true; setSelectedFurnitureId(fn.id); }}
                  onTap={(e) => { e.cancelBubble = true; setSelectedFurnitureId(fn.id); }}
                  data-testid={`furniture-${fn.id}`}
                />
                {w > 22 && (
                  <Text x={ctr.x} y={ctr.y} offsetX={w / 2 - 2} offsetY={h / 2 - 2} rotation={rot} width={Math.max(10, w - 4)} text={k.label} fontSize={Math.max(7, Math.min(10, h * 0.4))}
                        fill="#fff" listening={false} ellipsis wrap="none" />
                )}
                {isSel && (
                  <Circle
                    x={hx} y={hy} radius={6}
                    fill={k.color} stroke="#fff" strokeWidth={1.5}
                    draggable
                    onDragEnd={(e) => {
                      const dx = e.target.x() - ctr.x, dy = e.target.y() - ctr.y;
                      const lx = dx * Math.cos(-rad) - dy * Math.sin(-rad);
                      const ly = dx * Math.sin(-rad) + dy * Math.cos(-rad);
                      const nw = Math.max(0.005, px2norm(tl.x + Math.max(6, lx * 2), tl.y).x - fn.x);
                      const nh = Math.max(0.005, px2norm(tl.x, tl.y + Math.max(6, ly * 2)).y - fn.y);
                      actions.updateFurniture(fn.id, { w: Math.min(1 - fn.x, nw), h: Math.min(1 - fn.y, nh) });
                    }}
                    data-testid={`furniture-resize-${fn.id}`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Layer>

        {/* SKEW EDGES LAYER — V14.69 Phase 2 : lignes de brisure (mansardes).
            Sélection au clic, extrémités draggables quand sélectionnée. */}
        <Layer listening={activeSubTab === 'walls'}>
          {(skewEdges || []).map((se) => {
            const p1 = norm2px(se.x1, se.y1);
            const p2 = norm2px(se.x2, se.y2);
            const isSel = se.id === selectedSkewId;
            return (
              <React.Fragment key={se.id}>
                <Line
                  points={[p1.x, p1.y, p2.x, p2.y]}
                  stroke={isSel ? '#fde68a' : '#f59e0b'}
                  strokeWidth={isSel ? 4 : 3}
                  dash={[12, 6]}
                  hitStrokeWidth={16}
                  shadowColor="#f59e0b"
                  shadowBlur={isSel ? 10 : 0}
                  onClick={() => setSelectedSkewId(se.id)}
                  onTap={() => setSelectedSkewId(se.id)}
                />
                {isSel && ['1', '2'].map((suffix) => {
                  const p = suffix === '1' ? p1 : p2;
                  return (
                    <Circle
                      key={`${se.id}-ep${suffix}`}
                      x={p.x} y={p.y} radius={6}
                      fill="#f59e0b" stroke="#fff" strokeWidth={1.5}
                      draggable
                      onDragEnd={(e) => {
                        const n = px2norm(e.target.x(), e.target.y());
                        actions.updateSkewEdge(se.id, {
                          [`x${suffix}`]: Math.max(0, Math.min(1, n.x)),
                          [`y${suffix}`]: Math.max(0, Math.min(1, n.y)),
                        });
                      }}
                    />
                  );
                })}
              </React.Fragment>
            );
          })}
        </Layer>

        {/* OPENINGS LAYER */}
        <Layer listening={activeSubTab === 'openings'}>
          {openings.map((o) => {
            const style = openingStyleFor(o.type);
            const isSel = o.id === selectedOpeningId;
            const isHov = o.id === hoveredOpeningId;
            const center = norm2px(o.x, o.y);
            const wPx = Math.max(8, o.width * transform.scaleX);
            const hPx = Math.max(4, o.height * transform.scaleY);
            // V14.78 — étiquette largeur en cm (plans calibrés uniquement).
            const widthCm = scaleMPerNorm ? Math.round(o.width * scaleMPerNorm * 100) : null;
            return (
              <React.Fragment key={o.id}>
              <Rect
                id={`opening-${o.id}`}
                x={center.x}
                y={center.y}
                offsetX={wPx / 2}
                offsetY={hPx / 2}
                width={wPx}
                height={hPx}
                rotation={o.rotation || 0}
                fill={style.color}
                opacity={activeSubTab === 'openings' ? (isHov ? 0.95 : 0.78) : 0.45}
                stroke={isSel || isHov ? '#ffffff' : 'rgba(0,0,0,0.6)'}
                strokeWidth={isSel ? 2.5 : isHov ? 2 : 1}
                shadowColor={isSel || isHov ? style.color : undefined}
                shadowBlur={isSel || isHov ? 10 : 0}
                cornerRadius={2}
                draggable={activeSubTab === 'openings' && mode === 'select'}
                onDragEnd={(e) => handleOpeningDragEnd(o.id, e)}
                onClick={() => handleOpeningClick(o.id)}
                onTap={() => handleOpeningClick(o.id)}
                onMouseEnter={() => setHoveredOpeningId(o.id)}
                onMouseLeave={() => setHoveredOpeningId(null)}
              />
              {/* V15.30 — montants (limites de vantaux) sur les menuiseries vitrées */}
              {GLAZED_OPENING_TYPES.includes(o.type) && (() => {
                const { leaves } = openingSpecFor(o, scaleMPerNorm ? o.width * scaleMPerNorm : 0);
                if (!leaves || leaves < 2) return null;
                const rad = ((o.rotation || 0) * Math.PI) / 180;
                const cos = Math.cos(rad), sin = Math.sin(rad);
                const half = hPx / 2 + 1;
                return Array.from({ length: leaves - 1 }, (_, i) => {
                  const dx = -wPx / 2 + (wPx * (i + 1)) / leaves;
                  return (
                    <Line
                      key={`${o.id}-mullion-${i}`}
                      points={[center.x + dx * cos + half * sin, center.y + dx * sin - half * cos,
                               center.x + dx * cos - half * sin, center.y + dx * sin + half * cos]}
                      stroke="#0b0d12"
                      strokeWidth={1.5}
                      opacity={activeSubTab === 'openings' ? 0.9 : 0.5}
                      listening={false}
                    />
                  );
                });
              })()}
              {widthCm != null && activeSubTab === 'openings' && (
                <Text
                  x={center.x}
                  y={center.y}
                  offsetX={14}
                  offsetY={-(hPx / 2) - 3}
                  rotation={o.rotation || 0}
                  text={`${widthCm} cm`}
                  fontSize={9}
                  fontStyle="bold"
                  fill="#ffffff"
                  shadowColor="rgba(0,0,0,0.9)"
                  shadowBlur={3}
                  listening={false}
                />
              )}
              </React.Fragment>
            );
          })}
        </Layer>

        {/* ROOMS LAYER — V14.49 Sprint 4 */}
        <Layer listening={activeSubTab === 'rooms'}>
          {rooms.map((r) => {
            const style = roomStyleFor(r.type);
            const isSel = r.id === selectedRoomId;
            const isHov = r.id === hoveredRoomId;
            if (!r.polygon || r.polygon.length < 3) return null;
            const points = r.polygon.flatMap((p) => {
              const pt = norm2px(p[0], p[1]);
              return [pt.x, pt.y];
            });
            const baseOpacity = activeSubTab === 'rooms' ? 0.35 : 0.18;
            // V14.85 — étiquette « label + m² » au centroïde (plans calibrés).
            let roomLabel = null;
            if (scaleMPerNorm && (activeSubTab === 'rooms' || showDimensions)) {
              let area = 0; let cx = 0; let cy = 0;
              for (let i = 0, j = r.polygon.length - 1; i < r.polygon.length; j = i, i += 1) {
                const [xi, yi] = r.polygon[i];
                const [xj, yj] = r.polygon[j];
                const cross = xj * yi - xi * yj;
                area += cross;
                cx += (xi + xj) * cross;
                cy += (yi + yj) * cross;
              }
              area /= 2;
              if (Math.abs(area) > 1e-8) {
                cx /= (6 * area); cy /= (6 * area);
                const iw = imgDims?.w || 1000;
                const ih = imgDims?.h || 1000;
                const mPerPx = scaleMPerNorm / ((iw + ih) / 2);
                const m2 = Math.abs(area) * iw * ih * mPerPx * mPerPx;
                const c = norm2px(cx, cy);
                roomLabel = { x: c.x, y: c.y, m2, name: r.label || '' };
              }
            }
            return (
              <React.Fragment key={r.id}>
              <Line
                points={points}
                closed
                fill={style.color}
                opacity={isHov ? Math.min(0.6, baseOpacity + 0.2) : baseOpacity}
                stroke={isSel ? '#ffffff' : style.color}
                strokeWidth={isSel ? 3 : isHov ? 2 : 1.2}
                shadowColor={isSel || isHov ? style.color : undefined}
                shadowBlur={isSel || isHov ? 10 : 0}
                onClick={() => handleRoomClick(r.id)}
                onTap={() => handleRoomClick(r.id)}
                onMouseEnter={() => setHoveredRoomId(r.id)}
                onMouseLeave={() => setHoveredRoomId(null)}
              />
              {roomLabel && (
                <Text
                  x={roomLabel.x}
                  y={roomLabel.y}
                  offsetX={40}
                  offsetY={roomLabel.name ? 11 : 6}
                  width={80}
                  align="center"
                  text={roomLabel.name
                    ? `${roomLabel.name}\n${roomLabel.m2.toFixed(1)} m²`
                    : `${roomLabel.m2.toFixed(1)} m²`}
                  fontSize={10}
                  fontStyle="bold"
                  lineHeight={1.25}
                  fill="#ffffff"
                  shadowColor="rgba(0,0,0,0.95)"
                  shadowBlur={4}
                  listening={false}
                />
              )}
              </React.Fragment>
            );
          })}
        </Layer>

        {/* POLYGON DRAFT LAYER — V14.52 Sprint 7 */}
        {isPolygonDrawMode && polygonDraft.length > 0 && (
          <Layer listening={false}>
            {(() => {
              let draftColor;
              if (mode === 'draw-outer-polygon') draftColor = '#f59e0b';
              else if (mode === 'draw-terrain-polygon') draftColor = '#fbbf24';
              else if (mode === 'draw-polyline-walls') draftColor = draftWallStyle.color; // V14.91 — couleur du type tracé
              else draftColor = '#10b981';
              const wallThPx = mode === 'draw-polyline-walls'
                ? Math.max(2, draftWallStyle.thicknessImgPx * (transform.imgW / (imgDims?.w || transform.imgW))) : 0;
              const pts = polygonDraft.flatMap((p) => {
                const pt = norm2px(p[0], p[1]);
                return [pt.x, pt.y];
              });
              // include the live hint as the last point so the line follows the cursor
              if (draftHint && polygonDraft.length >= 1) {
                const h = norm2px(draftHint[0], draftHint[1]);
                pts.push(h.x, h.y);
              }
              return (
                <>
                  {wallThPx > 0 && <Line points={pts} stroke={draftColor} strokeWidth={wallThPx} opacity={0.3} lineCap="butt" lineJoin="miter" />}
                  <Line points={pts} stroke={draftColor} strokeWidth={2} dash={[6, 4]} />
                  {wallThPx > 0 && draftHint && (
                    <Text x={norm2px(draftHint[0], draftHint[1]).x + 10} y={norm2px(draftHint[0], draftHint[1]).y - 22}
                          text={draftWallStyle.label} fontSize={11} fill="#fff" fontStyle="bold" padding={3}
                          shadowColor="#000" shadowBlur={4} />
                  )}
                  {polygonDraft.map((p, i) => {
                    const pt = norm2px(p[0], p[1]);
                    return (
                      <Circle key={i} x={pt.x} y={pt.y}
                              radius={i === 0 ? 6 : 4}
                              fill={i === 0 ? '#ffffff' : draftColor}
                              stroke={draftColor}
                              strokeWidth={i === 0 ? 2 : 1} />
                    );
                  })}
                  {/* V14.79 — cote live du segment en cours (plan calibré) */}
                  {showDimensions && scaleMPerNorm && draftHint && polygonDraft.length >= 1 && (() => {
                    const [lx, ly] = polygonDraft[polygonDraft.length - 1];
                    const lenM = Math.hypot(draftHint[0] - lx, draftHint[1] - ly) * scaleMPerNorm;
                    if (lenM < 0.05) return null;
                    const a = norm2px(lx, ly);
                    const b = norm2px(draftHint[0], draftHint[1]);
                    return (
                      <Text
                        x={(a.x + b.x) / 2 + 8}
                        y={(a.y + b.y) / 2 - 12}
                        text={`${lenM.toFixed(2)} m`}
                        fontSize={11}
                        fontStyle="bold"
                        fill="#ffffff"
                        shadowColor="rgba(0,0,0,0.95)"
                        shadowBlur={4}
                      />
                    );
                  })()}
                </>
              );
            })()}
          </Layer>
        )}
      </Stage>

      {/* Help overlay */}
      {activeSubTab === 'walls' && mode === 'draw' && (
        <div className="absolute bottom-2 left-2 text-[10px] px-2 py-1 rounded bg-black/60 text-amber-200">
          {draftStart ? '2/2 · Cliquez l\u2019extr\u00e9mit\u00e9 du mur' : '1/2 · Cliquez le 1er point du mur'}
        </div>
      )}
      {/* V14.80 — Saisie de longueur au clavier (plans calibrés) */}
      {lengthEntryActive && (
        lengthInput ? (
          <div className="absolute bottom-2 right-2 flex items-center gap-1.5 text-[12px] px-2.5 py-1.5 rounded border border-amber-400/50 bg-black/85 text-amber-100 font-mono shadow-lg z-20"
               data-testid="length-entry-badge">
            <span className="text-amber-400">⌨</span>
            <strong>{lengthInput} m</strong>
            <span className="text-[10px] text-gray-400 font-sans">· Entrée = placer · Échap = annuler</span>
          </div>
        ) : (
          <div className="absolute bottom-2 right-2 text-[10px] px-2 py-1 rounded bg-black/60 text-gray-400 z-20"
               data-testid="length-entry-hint">
            Astuce : tapez une longueur (ex. <span className="text-amber-300 font-mono">3.5</span>) puis Entrée
          </div>
        )
      )}
      {mode === 'draw:skew' && (
        <div className="absolute bottom-2 left-2 text-[10px] px-2 py-1 rounded bg-black/60 text-amber-200"
             data-testid="canvas-hint-skew">
          {draftStart
            ? '2/2 · Cliquez la fin de la ligne de brisure (Shift = orthogonal)'
            : '1/2 · Cliquez le début de la ligne de brisure (mansarde)'}
        </div>
      )}
      {/* Zoom controls — socle commun (V15.39) */}
      <CanvasZoomControls viewport={viewport} zoomBy={zoomBy} resetView={resetView} testIdPrefix="canvas" />

      {activeSubTab === 'openings' && isPlaceMode && (
        <div className="absolute top-2 left-2 text-[10px] px-2 py-1 rounded bg-black/70 text-emerald-200 pointer-events-none select-none shadow-lg z-20"
             data-testid="canvas-hint-openings">
          Cliquez sur le plan pour placer une <strong>{openingStyleFor(placeType).label}</strong>
        </div>
      )}
      {activeSubTab === 'furniture' && mode.startsWith('place:furniture:') && (
        <div className="absolute top-2 left-2 text-[10px] px-2 py-1 rounded bg-black/70 text-emerald-200 pointer-events-none select-none shadow-lg z-20"
             data-testid="canvas-hint-furniture">
          Cliquez dans une pièce pour poser <strong>{furnitureKindFor(mode.slice('place:furniture:'.length)).label}</strong> à ses dimensions réelles
        </div>
      )}
      {isPolygonDrawMode && (
        <div className="absolute top-2 left-2 text-[10px] px-2 py-1 rounded bg-black/70 text-emerald-200 pointer-events-none select-none shadow-lg z-20"
             data-testid="canvas-hint-polygon">
          {isWallPolylineMode
            ? (polygonDraft.length === 0
                ? 'Polyligne mur · cliquez le 1er point · double-clic ou Entrée = terminer · Échap = annuler'
                : `${polygonDraft.length} point(s) · double-clic / Entrée = créer ${Math.max(0, polygonDraft.length - 1) + (draftHint ? 1 : 0)} mur(s) · Échap = annuler`)
            : (polygonDraft.length === 0
                ? 'Cliquez pour placer le 1er sommet · Shift = orthogonal'
                : `${polygonDraft.length} sommet(s) · double-clic OU clic sur le 1er point pour fermer · Shift = orthogonal`)}
        </div>
      )}
    </div>
  );
};

export default StructureCanvas;
